import { useCallback, useEffect, useRef, useState } from "react";
import { fetchGrantDetail, fetchGrants } from "../../api/admin-governance";
import type { GrantRecord } from "../../api/admin-governance";
import {
  Pagination,
  Td,
  Th,
  fmtDate,
  shortId,
  tableStyles,
} from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { StatsBar } from "../../components/StatsBar";
import { t } from "../../i18n";

const PER_PAGE = 20;

interface Filters {
  user_id: string;
  agent_id: string;
  scope_type: string;
  status: string;
}

const EMPTY_FILTERS: Filters = {
  user_id: "",
  agent_id: "",
  scope_type: "",
  status: "",
};

export function GrantsPage(): React.JSX.Element {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [grants, setGrants] = useState<GrantRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<GrantRecord | null>(null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const load = useCallback(async (p: number): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchGrants({
        ...stripEmpty(filtersRef.current),
        page: p,
        per_page: PER_PAGE,
      });
      setGrants(res.items);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.failed_to_load"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(page);
  }, [page, load]);

  const handleApply = useCallback((): void => {
    setPage(1);
    void load(1);
  }, [load]);

  const handleExpand = useCallback(
    async (id: string): Promise<void> => {
      if (expandedId === id) {
        setExpandedId(null);
        setDetail(null);
        return;
      }
      setExpandedId(id);
      try {
        setDetail(await fetchGrantDetail(id));
      } catch {
        setDetail(null);
      }
    },
    [expandedId],
  );

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div>
      <PageHeader
        title={t("grants.title")}
        description={t("grants.description")}
      />
      <StatsBar items={[{ label: t("stats.total"), value: total }]} />
      <FilterBar
        filters={filters}
        onChange={setFilters}
        onApply={handleApply}
      />
      {error && <p style={s.error}>{error}</p>}
      <table style={s.table}>
        <thead>
          <tr>
            <Th>{t("grants.col_grant_id")}</Th>
            <Th>{t("grants.col_user")}</Th>
            <Th>{t("grants.col_agent")}</Th>
            <Th>{t("grants.col_scope")}</Th>
            <Th>{t("grants.col_expires")}</Th>
            <Th>{t("grants.col_status")}</Th>
          </tr>
        </thead>
        <tbody>
          {grants.map((g) => (
            <GrantRow
              key={g.id}
              grant={g}
              isExpanded={expandedId === g.id}
              detail={expandedId === g.id ? detail : null}
              onToggle={handleExpand}
            />
          ))}
          {!loading && grants.length === 0 && (
            <tr>
              <Td colSpan={6}>{t("grants.no_grants_found")}</Td>
            </tr>
          )}
        </tbody>
      </table>
      {loading && <p style={s.loadingText}>{t("common.loading")}</p>}
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}

/* ---------- Sub-components ---------- */

function FilterBar({
  filters,
  onChange,
  onApply,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
  onApply: () => void;
}): React.JSX.Element {
  const set = (key: keyof Filters, val: string): void => {
    onChange({ ...filters, [key]: val });
  };

  return (
    <div style={s.filterRow}>
      <input
        placeholder={t("grants.placeholder_user_id")}
        value={filters.user_id}
        onChange={(e) => set("user_id", e.target.value)}
        style={s.filterInput}
      />
      <input
        placeholder={t("grants.placeholder_agent_id")}
        value={filters.agent_id}
        onChange={(e) => set("agent_id", e.target.value)}
        style={s.filterInput}
      />
      <input
        placeholder={t("grants.placeholder_scope_type")}
        value={filters.scope_type}
        onChange={(e) => set("scope_type", e.target.value)}
        style={s.filterInput}
      />
      <input
        placeholder={t("grants.placeholder_status")}
        value={filters.status}
        onChange={(e) => set("status", e.target.value)}
        style={s.filterInput}
      />
      <button type="button" onClick={onApply} style={s.filterBtn}>
        Apply
      </button>
    </div>
  );
}

function GrantRow({
  grant,
  isExpanded,
  detail,
  onToggle,
}: {
  grant: GrantRecord;
  isExpanded: boolean;
  detail: GrantRecord | null;
  onToggle: (id: string) => void;
}): React.JSX.Element {
  return (
    <>
      <tr
        onClick={() => onToggle(grant.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onToggle(grant.id);
        }}
        tabIndex={0}
        style={{ cursor: "pointer" }}
      >
        <Td mono>{shortId(grant.id)}</Td>
        <Td mono>{shortId(grant.user_id)}</Td>
        <Td mono>{shortId(grant.agent_id)}</Td>
        <Td>{grant.scope_type}</Td>
        <Td>
          {grant.expires_at ? fmtDate(grant.expires_at) : t("grants.never")}
        </Td>
        <Td>{grant.status}</Td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={6} style={s.expandedCell}>
            <GrantDetail detail={detail} />
          </td>
        </tr>
      )}
    </>
  );
}

function GrantDetail({
  detail,
}: {
  detail: GrantRecord | null;
}): React.JSX.Element {
  if (!detail)
    return <p style={s.detailLoading}>{t("grants.loading_detail")}</p>;
  const jobs = detail.summary_jobs ?? [];
  return (
    <div style={s.detailBox}>
      <p style={s.detailField}>
        <strong>{t("grants.scope_value")}</strong> {detail.scope_value}
      </p>
      <p style={s.detailField}>
        <strong>{t("grants.created")}</strong> {fmtDate(detail.created_at)}
      </p>
      {jobs.length > 0 && (
        <>
          <p style={s.detailSubtitle}>{t("grants.summary_jobs")}</p>
          <ul style={s.detailList}>
            {jobs.map((j) => (
              <li key={j.id}>
                {shortId(j.id)} - {j.status} ({fmtDate(j.created_at)})
              </li>
            ))}
          </ul>
        </>
      )}
      {jobs.length === 0 && (
        <p style={s.detailMuted}>{t("grants.no_summary_jobs")}</p>
      )}
    </div>
  );
}

/* ---------- Helpers ---------- */

function stripEmpty(obj: Filters): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v) out[k] = v;
  }
  return out;
}

/* ---------- Styles ---------- */

const s: Record<string, React.CSSProperties> = {
  ...tableStyles,
  filterInput: { ...tableStyles.filterInput, width: 160 },
  expandedCell: {
    padding: "0 12px 12px 12px",
    background: "var(--color-bg-secondary)",
  },
  detailBox: { padding: 12 },
  detailLoading: { fontSize: 13, color: "var(--color-text-secondary)" },
  detailField: { fontSize: 13, marginBottom: 4 },
  detailSubtitle: {
    fontSize: 13,
    fontWeight: 600,
    marginTop: 8,
    marginBottom: 4,
  },
  detailList: {
    fontSize: 13,
    paddingLeft: 16,
    fontFamily: "var(--font-mono)",
  },
  detailMuted: {
    fontSize: 13,
    color: "var(--color-text-secondary)",
    marginTop: 4,
  },
};
